/**
 * DOCTOR-WORKSPACE-COMPLETION-V1 -- the route guard for the doctor lens
 * surfaces (`/dashboard/doctor/patients`, `/dashboard/doctor/patient/:id`).
 *
 * Mirrors RequirePatientMaster: the pure decision lives in
 * doctor-patient-lens-view.ts and keys off the SERVER-computed
 * `me.doctorLensEnabled` signal (present ONLY for a scoped doctor). Absent
 * (unscoped doctor, or any other role) -> redirect to the caller's own
 * homePath, so the door is never a dead link and no lower role reaches the
 * lens by URL. Backend route auth (requireDoctorWorkspaceScope) remains the
 * real security boundary; this guard is UX cleanliness only.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  decideDoctorLensAccess,
  type DoctorLensAccessDecision,
} from '@/dashboard/doctor/doctor-patient-lens-view';
import type { DashboardMe } from '@/dashboard/types';

export interface RequireDoctorLensViewProps {
  readonly loading: boolean;
  readonly error: string | null;
  readonly me: DashboardMe | null;
  readonly children?: React.ReactNode;
}

function renderDecision(
  decision: DoctorLensAccessDecision,
  children: React.ReactNode,
): JSX.Element | null {
  switch (decision.kind) {
    case 'wait':
      return null;
    case 'redirect':
      return <Navigate to={decision.to} replace />;
    case 'render':
      return <>{children ?? <Outlet />}</>;
    default: {
      const _exhaustive: never = decision;
      void _exhaustive;
      return null;
    }
  }
}

/** Pure presentational guard -- testable via react-dom/server. */
export function RequireDoctorLensView({
  loading,
  error,
  me,
  children,
}: RequireDoctorLensViewProps): JSX.Element | null {
  return renderDecision(decideDoctorLensAccess({ loading, error, me }), children);
}

export default function RequireDoctorLens(): JSX.Element | null {
  const ctx = useDashboardMeContext();
  return (
    <RequireDoctorLensView
      loading={ctx?.loading ?? true}
      error={ctx?.error ?? null}
      me={ctx?.me ?? null}
    />
  );
}
